import { motion } from "framer-motion";

const CAPABILITIES = [
  { n: "01", name: "Product & engineering pods", desc: "Cross-functional teams working inside your product organisation." },
  { n: "02", name: "Design & creative teams", desc: "Craft-led teams aligned to your brand system and review practice." },
  { n: "03", name: "Marketing & content operations", desc: "Content, campaign, and community capability tied into your delivery model." },
  { n: "04", name: "Data & analytics capability", desc: "A focused team supporting your reporting, insights, and modelling needs." },
  { n: "05", name: "Operations & delivery support", desc: "Back-office and delivery functions that keep your global work moving." },
  { n: "06", name: "Specialist Centres of Excellence", desc: "A concentrated capability around a discipline or practice area." },
];

export default function Capabilities() {
  return (
    <section id="capabilities" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-[#F4F0FE]" data-testid="capabilities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-14"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            What we help set up
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#0B132B] leading-[1.04]">
            Designed around the work you need to build.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed">
            Rather than list unverified statistics, here are the kinds of teams and functions we
            typically help international businesses set up on the ground.
          </p>
        </motion.div>

        <div className="rounded-[32px] bg-white shadow-[0_24px_60px_-24px_rgba(76,29,149,0.18)] overflow-hidden">
          {CAPABILITIES.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="group grid grid-cols-[auto_1fr] sm:grid-cols-[110px_1fr_1.2fr] gap-4 sm:gap-8 items-center py-6 border-b border-[#0B132B]/[0.05] last:border-b-0 hover:bg-[#F6F3FE] transition-colors duration-300 px-5 sm:px-8"
              data-testid={`capability-row-${i}`}
            >
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-full border-2 border-[#5B4199]/30 font-mono-tech text-xs text-[#5B4199] group-hover:bg-[#5B4199] group-hover:text-white group-hover:border-[#5B4199] transition-colors duration-300">
                {c.n}
              </span>
              <h3 className="font-display text-xl sm:text-2xl font-extrabold text-[#0B132B] group-hover:text-[#5B4199] transition-colors duration-300">
                {c.name}
              </h3>
              <p className="col-span-2 sm:col-span-1 text-sm sm:text-[15px] text-[#475569] leading-relaxed">
                {c.desc}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-8 text-xs text-[#94A3B8] max-w-2xl"
        >
          Documented case studies and named references are shared under NDA on request.
        </motion.p>
      </div>
    </section>
  );
}
